import { AxiosError } from 'axios';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { instance } from 'src/api/instance';
import { useGetUserInfo } from 'src/api/user';
import { TLoginResponse } from 'src/types/auth';
import { TUser } from 'src/types/user';
import type { State } from './auth-context';
import { AuthContext, initialState } from './auth-context';
import { Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { paths } from 'src/routes/paths';
import { useNavigate } from 'react-router';
import { calculateDays } from 'src/utils/format-time';
import ExpirePlanModal from 'src/components/expire-plan-modal';

export const STORAGE_KEY = 'accessToken';

enum ActionType {
  INITIALIZE = 'INITIALIZE',
  SIGN_IN = 'SIGN_IN',
  SET_INFO = 'SET_INFO',
  SIGN_OUT = 'SIGN_OUT',
}

interface InitializeAction {
  type: ActionType.INITIALIZE;
  payload: {
    isAuthenticated: boolean;
    user: Partial<TUser> | null;
  };
}

interface SignInAction {
  type: ActionType.SIGN_IN;
  payload: {
    user: Partial<TUser>;
  };
}
interface SetInfoAction {
  type: ActionType.SET_INFO;
  payload: {
    user: Partial<TUser>;
  };
}

interface SignOutAction {
  type: ActionType.SIGN_OUT;
}

type Action = InitializeAction | SetInfoAction | SignInAction | SignOutAction;

type Handler = (state: State, action: any) => State;

const handlers: Record<ActionType, Handler> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SIGN_IN: (state: State, action: SignInAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SET_INFO: (state: State, action: SetInfoAction): State => {
    const { user } = action.payload;
    return {
      ...state,
      user,
    };
  },
  SIGN_OUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type](state, action);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = props => {
  const { children } = props;
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const accessToken = window.localStorage.getItem(STORAGE_KEY);

  const type = window.localStorage.getItem('type');
  const id = window.localStorage.getItem('id');
  if (accessToken) {
    instance.defaults.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (type) {
    instance.defaults.headers.type = type;
    instance.defaults.headers.id = id;
  }
  const { data: userInfoData, error: userInfoError } = useGetUserInfo({
    enabled: !!accessToken,
  });
  const [state, dispatch] = useReducer(reducer, initialState);
  const signOut = useCallback((): void => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('type');
    localStorage.removeItem('id');
    queryClient.clear();
    delete instance.defaults.headers.Authorization;
    delete instance.defaults.headers.common.Authorization;
    instance.defaults.headers.common.Authorization = '';
    instance.defaults.headers.common.type = '';

    delete instance.defaults.headers.common.type;
    delete instance.defaults.headers.common.id;
    instance.defaults.headers.common.id = '';
    dispatch({ type: ActionType.SIGN_OUT });
  }, [dispatch]);

  instance.interceptors.response.use(
    response =>
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      response,
    (error: AxiosError) =>
      new Promise(() => {
        if (
          error?.response?.status === 401 &&
          error?.response?.config?.url?.search('login') === -1
        ) {
          // instance.defaults.headers

          signOut();
        } else {
          throw error;
        }
      }),
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
  );

  useEffect(() => {
    if (userInfoData) {
      // if (i18n.language !== userInfoData.data.selected_language.language_code) {
      //   i18n.changeLanguage(userInfoData.data.selected_language.language_code);
      // }
      if (userInfoData.data.current_carrier?.is_check_lock_trucks) {
        navigate(paths.dashboard.selectUnlockItems);
      }

      if (
        userInfoData.data.current_plan &&
        (calculateDays(userInfoData.data.current_plan.expired) === 0 ||
          calculateDays(userInfoData.data.current_plan.expired) < 0)
      ) {
        const isShowPopup = localStorage.getItem('plan-show') || 'true';
        if (isShowPopup) {
          localStorage.setItem('plan-show', 'false');
          navigate(`${paths.dashboard.subscriptionPlan}?expired=true`);
        }
      }
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: true,
          user: userInfoData.data,
        },
      });
    }
  }, [i18n, userInfoData]);

  const signIn = useCallback(
    async (userData: TLoginResponse['data']['info']): Promise<void> => {
      localStorage.setItem('type', userData.current_type || '');
      localStorage.setItem('id', userData.current_id?.toString() || '');
      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user: userData,
        },
      });
    },
    [],
  );

  const setInfo = useCallback(
    async (userData: TLoginResponse['data']['info']): Promise<void> => {
      localStorage.setItem(STORAGE_KEY, userData.access_token);
      localStorage.setItem('type', userData.current_type || '11');
      localStorage.setItem('id', userData.current_id?.toString() || '11');
      dispatch({
        type: ActionType.SET_INFO,
        payload: {
          user: userData,
        },
      });
    },
    [],
  );

  useEffect(() => {
    if (!accessToken) {
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [accessToken]);

  const contextValue = useMemo(
    () => ({ ...state, signIn, signOut, setInfo }),
    [signIn, setInfo, signOut, state],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {userInfoError ? (
        <Typography>{userInfoError.message}</Typography>
      ) : (
        children
      )}
     
    </AuthContext.Provider>
  );
};
